/** CrmRecordTimeline — the consolidated record timeline for a DEAL record, per notes-provenance
 * spec §8. One stream, three lanes merged CLIENT-SIDE by sort key.
 *
 * E1b-2 (owner ruling 2026-08-21): this is a SECTION OF THE RECORD PANEL, not a sub-tab — it renders
 * under the fields grid in the same scroll, with **+ Note** in its header (the only note-creation
 * entry point on a deal, now that the Notes sub-tab is gone).
 *
 * ⛔ AND THEREFORE: NO PINNED PENDING QUEUE. Spec §8 put one on top, which was right for a tab and
 * wrong for a section. Its population was exactly the two qualitative blocks, and those same pending
 * proposals render in the FIELDS GRID a few inches above this — inline at their layout position on
 * `record_review.v2`, or in the `CrmRecentUpdates` cluster when v2 is off, which is also the surface
 * carrying ✓ Accept. On a tab the two never shared a screen; in one scroll they are the same rows
 * twice, which is the one-record-contradicting-itself class this panel keeps fixing. Dropping the
 * queue also DISSOLVED E1a's open owner question (§8's inline accept/edit-accept/reject could not be
 * built as written, because S4/R5b-1 sanction none of them for a block): there is no queue to put an
 * accept in, and pending is decided where the server already allows it. Do not re-add a pending
 * section here without moving the grid's.
 *
 * ⛔ THE LOAD-BEARING DESIGN CONSTRAINT: every lane keeps its OWN governed door. There is no
 * server-side merge endpoint composing gates (considered and rejected for this slice), so this view
 * can only ever show what the caller could already fetch from each lane's own endpoint:
 *   · evidence  → GET /crm/lists/{id}/entries/{eid}/evidence      (D8 row gate + per-mailbox §9 gate)
 *   · updates   → GET /crm/lists/{id}/entries/{eid}/fields/{k}/history  (entry visibility, same
 *                 predicate the record's own hydrate read uses — `_entry_in_list`)
 *   · notes     → GET /crm/notes?target_kind=list_entry&target_id={eid}  (scoped_note_select)
 * A looser lane never widens a stricter one: the lanes are separate fetches, separately gated, and a
 * lane that refuses degrades to a notice ON ITS OWN ROW rather than blanking the stream.
 *
 * Sort keys (spec §8): evidence → `occurred_at`; accepted updates → the version row's `occurred_at`,
 * which IS the decision time (`append_value_version` stamps it at resolve); notes → `created_at`.
 * Newest first.
 *
 * THE ACCEPTED-UPDATES LANE IS THE VERSION STREAM, not the materialized notes. Both exist: accepting a
 * Progress / New-Info / Next-Steps proposal appends a `crm_field_value_version` row AND materializes a
 * CRM note linked to the same list_entry (`proposal_lifecycle._maybe_materialize_block_note`). Spec §8
 * offers either ("rendered from accepted proposals or their materialized system notes") but rendering
 * both double-prints every accepted update. The version stream wins because it carries decided_at +
 * actor + method (human vs scheduled_job), which the note payload does not.
 *
 * ⚠ The twin therefore has to be kept out of the notes lane, and `list_notes` exposes NO marker for it:
 * `origin` / `source_proposal_id` are stamped only on the AUTO-accept path (`system_create_note`); a
 * HUMAN accept goes through plain `create_note`, authored by that human, and is byte-indistinguishable
 * from a note they typed — and neither column is on the wire anyway. So suppression is a PAIRED display
 * heuristic (label word-boundary + same moment), never unconditional; the reasoning and the failure
 * ordering live on `recordTimelineMerge.notesLaneItems`. The clean fix is an additive `origin` +
 * `source_proposal_id` on the notes list payload plus stamping them on the human path — deliberately
 * NOT done in E1a; it belongs with E1b.
 *
 * E1b-0 (2026-08-21) — the evidence lane carries the ⚑ REFUTE, so retiring the Evidence sub-tab
 * deletes no action. Two properties are load-bearing, both stated where they are implemented: the ⚑
 * renders from the server's per-row `can_refute` (`may_refute_event`), the same
 * affordance-follows-the-server rule the fields grid obeys; and the DIALOG IS OWNED BY THIS
 * COMPONENT, not by the row — a successful refute removes the row from the evidence payload, and a
 * dialog living inside it would unmount with the confirmation still unread.
 *
 * E1b-3 S3 (2026-08-22) — a SECOND filter axis: how far back the stream reaches. Five preset chips
 * plus two cross-constrained pickers, one `TimelineRange` in state. All the boundary reasoning (local
 * days, inclusive `to`, four counts, refusing an impossible range) lives on `splitByRange`, which this
 * only consumes. A CHOSEN range means exactly what it says, which is why the `older` / `newer` counts
 * below are buttons rather than notes and why the empty state names the range: a filter the operator
 * set must always be escapable in one click from the emptiness it caused.
 *
 * E1b-3 S4 (2026-08-23) — the lanes FETCH ON FIRST VISIBILITY, and the stream is WINDOWED.
 *
 * ⭐ THE OPENING VIEW IS NOT A RANGE SELECTION (owner ruling 2026-08-23, superseding the 30-day
 * default S3 shipped). It is `max(last 30 days, most recent 50 items)` — a date floor with an item
 * floor underneath it — held as its own axis state (`Recent`), because a picker reading `30 days`
 * while the view reaches nine months back is a control lying about what is on screen. So:
 *   · `rangeChosen === false` ⇒ NO date filter at all; the window's floor is `max(rows in the last 30
 *     days, 50)`, and every row past it is held by the WINDOW, whose `+ Show N more` is the escape.
 *   · The item floor is measured AFTER the kind filter, or a Notes-only view on a quiet deal drops
 *     under 50 rows and the floor never fires.
 *   · Choosing any preset or a custom range REPLACES the opening state rather than refining it, and
 *     from then on the range means exactly what it says (S3's footers, unchanged).
 *   · Therefore G5c's "Nothing in the last 30 days" can no longer fire on open — an empty opening
 *     stream now means the deal is genuinely empty, and "Nothing recorded on this deal yet" is the
 *     true sentence again. The standing rule that a quiet/unclickable escape reverts the default to
 *     All time is satisfied by construction: the opening view filters no dates away.
 *
 * ⛔ DEFERRED IS NOT EMPTY. With the lanes disabled, `shown.length === 0 && !isLoading` is true before
 * anything has been asked, so the not-yet-visible branch renders its OWN marker
 * (`crm-timeline-deferred`) and NOTHING else — the stream, the footers and the empty state are all
 * inside the visible branch, so "nothing is rendered until someone asks" is a property of the tree
 * rather than of five payloads happening to be undefined. Printing "nothing here" on a deal with
 * history is the one regression the deferral can cause.
 *
 * E1b-3 S2 (2026-08-22) — every row's NARRATIVE is height-clamped (`ClampedBlock`, 160px; the
 * expanded evidence body gets its own 240px clamp). The clamp is measured, so a row that fits shows
 * no control at all. ⛔ Nothing governed and nothing clickable is inside it: row heads, badges, the ⚑
 * and both expanders sit outside, and the clamp is keyed by `entryId` + the item key because an
 * updates row's key is entry-scoped and would otherwise collide across a ‹ › hop.
 *
 * WP-7 / lever F (2026-09-05) — a THIRD filter axis: WHICH PARTY. The chips come from the evidence
 * payload's `party_filter` block (the server sources them from this viewer's gated `parties` tree,
 * falling back to the catch-all cells), the counts are the server's, and the selection is transient
 * like `kinds` (D-11: no URL param). Two properties are load-bearing:
 *   · **D-12 — a party selection filters the EVIDENCE LANE ONLY.** Notes and accepted updates are
 *     not mail and have no party, so a selection LOCKS the kind chips to Evidence and the empty
 *     state says so, rather than silently hiding two lanes the operator can still see counts for.
 *   · **The chips render only when the server sent the block.** Its absence means the flag is dark —
 *     which is a different statement from "this deal has no parties", and the difference is exactly
 *     what stops a dark deploy from painting an empty control.
 * The selection lives in `crmPartyFilter`'s context because `CrmPartiesField`, several inches above
 * in the same scroll, sets it through "show emails" — the entry point spec lever F names.
 *
 * STILL OUT OF SCOPE, on purpose: label / #tag filters (spec §8 lists them; there is no per-note
 * label read on this surface yet — kind is the only filter here); raw email/meeting items scoped to
 * the list_entry (E2 — no read model exists); and the two display heuristics E1a documented, which
 * retire with one additive backend field each (notes `origin`/`source_proposal_id`,
 * `field_history.source_proposal_id`).
 *
 * ⚠ THE COST OF BEING A SECTION, and how S4 pays it: as a section the lanes fetched when the RECORD
 * opened rather than when a tab was clicked, and three of the four reads are audited
 * (`record_evidence`, `note_view`) — so an audit row meant "opened this record" rather than "asked to
 * see its evidence", and a ‹ › hop paid four lane requests per record for a section nobody scrolled
 * to. `useOnceVisible` (latched, per-record, failing open) narrows that back: an audited lane read now
 * means "scrolled to this deal's timeline", which is NARROWER than the pre-E1b-2 tab click and
 * discloses nothing new — same viewer, same governed doors, same payloads.
 */
import { useEffect, useRef, useState } from 'react';

import { useSelectionParam } from '../../hooks/navigation/useUrlState';
import {
  crmRecordEvidenceContentBase, useCrmContentOpenKey, useCrmFieldHistory, useCrmRecordEvidence,
  useCrmRecordEvidenceBody, useCrmRecordEvidenceContent, useCrmRecordEvidenceContentKey,
  useEntityNotes,
} from '../../hooks/useCrm';
import { useOnceVisible } from '../../hooks/useOnceVisible';
import { ClampedBlock } from '../shared/ClampedBlock';
import { useCrmContentOnDemand } from '../../hooks/useCrmContentOnDemand';
import { EmailAttachments } from '../shared/EmailAttachments';
import { EvidenceBody, evidenceBodyState } from '../shared/EvidenceBody';
import { attachmentListStateFromQuery } from '../shared/attachmentDisplay';
import { useCrmPartyFilter } from './crmPartyFilter';
import { CrmNoteOverlay } from './CrmNoteOverlay';
import { CrmBlockRuns } from './CrmBlockRuns';
import { CrmRefuteEvidenceModal } from './CrmRefuteEvidenceModal';
import {
  evidenceLaneItems, fmtWhen, laneError, mergeTimeline, notesLaneItems, RANGE_ALL, RANGE_DEFAULT,
  RANGE_PRESETS, splitByRange, updatesLaneError, updatesLaneItems, UPDATE_LABELS,
  type TimelineKind, type TimelineRange,
} from './recordTimelineMerge';
import styles from './CrmRecordTimeline.module.css';
import type { MentionContextTarget } from './crmNoteMention';


export type { TimelineKind };

const KIND_CHIPS: readonly { kind: TimelineKind; label: string }[] = [
  { kind: 'evidence', label: 'Evidence' },
  { kind: 'updates', label: 'Updates' },
  { kind: 'notes', label: 'Notes' },
];

/** How the empty state NAMES the active range, read off `RANGE_PRESETS` rather than spelled a second
 * time: two copies of "30 days" drift the moment the owner adjusts a preset, and this sentence has to
 * agree with the chip the operator just clicked or it is a lie about the control beside it. Only ever
 * called for a range that actually held rows back, so `all` never reaches it. */
function rangeName(range: TimelineRange): string {
  if (range.preset === 'custom') return 'this date range';
  const label = RANGE_PRESETS.find((p) => p.key === range.preset)?.label;
  return label ? `the last ${label.toLowerCase()}` : 'this date range';
}

/** The notes lane deliberately takes `useEntityNotes`' DEFAULT page size, so its React Query key is
 * byte-identical to `CrmNotesSection`'s (review finding 5). A forked key (this asked for 100) meant a
 * second fetch of the same governed read on a sub-tab switch — and with it a second `crm_access_audit`
 * `note_view` row. Audit noise on a governed read is the part that actually matters: it misleads
 * whoever reconstructs who read what, later, when nobody remembers there were two components.
 *
 * The cost is a smaller page on a lane that also FILTERS client-side (twin suppression), so the notice
 * driven by `has_more` below is load-bearing rather than cosmetic. Raising the page for BOTH surfaces
 * belongs in E1b, which owns notes display once the separate block goes. */
const NOTES_PAGE = 20;

/** E1b-3 D4 — the clamp caps are PER-SURFACE CONSTANTS by design, not one shared default: a stream
 * row, an expanded email body and a Recent-Updates block are read at different lengths, and the panel
 * already sizes their bullets differently. They are also the thing the owner expects to adjust once
 * they are on screen (build plan, open item 3), so they are named here rather than inlined. */
const ROW_CLAMP = 160;
const EVIDENCE_BODY_CLAMP = 240;

/** E1b-3 D2 — the window: how many merged rows render, and how many one click adds. Both are the same
 * 50 by owner ruling (build plan, open item 2). `WINDOW_STEP` is also the ITEM FLOOR of the opening
 * view: `max(last 30 days, most recent 50)`. */
const WINDOW_STEP = 50;

/** D-12, said once. Both the disabled chips and the tests read this, so the rule and its wording
 * cannot drift apart. */
const KIND_LOCK_HINT =
  'A party selection filters emails only — notes and updates aren’t filtered by a party';

/** F.5 — ONE evidence row's body, fetched because this component MOUNTED (on the click), through the
 * evidence lane's own audited door. Deliberately a child rather than a hook on the stream: it makes
 * "nothing is fetched until someone asks" a property of the tree instead of a convention. */
function TimelineEvidenceBody({ listId, entryId, eventLogId }: {
  listId: number; entryId: number; eventLogId: number;
}) {
  const q = useCrmRecordEvidenceBody(listId, entryId, eventLogId, true);
  // E1b-3 D4 — NO `key` is needed on this clamp, and that is a property of the tree rather than luck:
  // this component mounts on the click and unmounts on collapse, and `openBodies` is host-scoped and
  // reset DURING RENDER, so a ‹ › hop takes it down too. There is no surviving instance for a second
  // record's body to be swapped into, which is the reuse hazard ClampedBlock's header warns about.
  // EC-6 — the record surface's OWN content adapter. Same shared renderer as the email surfaces,
  // a completely different door: `/lists/{l}/entries/{e}/evidence/{ev}/email-content`, which
  // intersects the record gate with the parent email's gate server-side.
  //
  // ⛔ IT NEVER FALLS BACK TO THE GENERIC EMAIL ROUTE. If this door refuses, the answer is "no
  // documents shown" — not "try the other door" (spec §9). That is why `contentBase` is threaded
  // down rather than derived from a message id: this component has no message id to derive one
  // from, and giving it one would be handing it the bypass.
  const content = useCrmRecordEvidenceContent(listId, entryId, eventLogId, true);
  const contentState = attachmentListStateFromQuery(content);
  const statusKey = useCrmRecordEvidenceContentKey(listId, entryId, eventLogId);
  const base = crmRecordEvidenceContentBase(listId, entryId, eventLogId);
  const openKey = useCrmContentOpenKey(`record:${listId}:${entryId}:${eventLogId}`);
  const onDemand = useCrmContentOnDemand({
    base,
    openKey,
    content: contentState === 'ready' ? content.data : undefined,
    statusKey,
    // ⛔ THE BODY QUERY IS RE-READ TOO (review MINOR 9). This surface renders the evidence body,
    // and since EC-6 that door serves the CRM stored body once the 30-day excerpt is gone — so a
    // body acquisition that lands has to invalidate the thing that displays it, or the row keeps
    // showing "no body" until the next deliberate open and the auto-POST looks like it did nothing.
    extraInvalidateKeys: [['crm', 'record-evidence-body', listId, entryId, eventLogId]],
    enabled: true,
  });
  return (
    <>
      <ClampedBlock maxHeight={EVIDENCE_BODY_CLAMP} label="this message">
        <EvidenceBody className={styles.rowBody} state={evidenceBodyState(q)} body={q.data?.body} />
      </ClampedBlock>
      {/* `emailId` is 0 because this surface deliberately does not know one — every read it makes
          is addressed by (list, entry, event), and the byte door resolves the canonical message
          itself. A message id here would be the generic route's key, i.e. the bypass. */}
      <EmailAttachments
        emailId={0}
        state={contentState}
        content={contentState === 'ready' ? content.data : undefined}
        contentBase={base}
        onDemand={onDemand}
      />
    </>
  );
}

interface Props {
  listId: number;
  entryId: number;
  hostLabel?: string | null;
  mentionContext?: MentionContextTarget[];
}

/** ⛔ READ-ONLY BY DESIGN (E1b-2). The only write reachable from this surface is the ⚑ refute (its
 * own dialog, its own governed door, its own invalidation) and creating a note. Accept / dismiss of a
 * pending proposal is the FIELDS GRID's job, above — which is why the props that carried the pending
 * sources, the R2b CAS token and the post-write refetch are gone rather than unused. */
export function CrmRecordTimeline({ listId, entryId, hostLabel, mentionContext }: Props) {
  // ⛔ D1 — ALL FOUR governed lane reads hang off this one latch, and `resetKey` is REQUIRED: this
  // component survives an `entryId` change without remounting, so a latch without the host identity
  // would stay latched for the session and every later record in a ‹ › hop would fire all four reads
  // on open — turning "this audit row means someone scrolled to the timeline" into a falsehood after
  // the first record. It fails open (no `IntersectionObserver` ⇒ visible in render 1), so the worst
  // case is exactly the behaviour that shipped before this slice.
  //
  // `root` is deliberately NOT passed (the hook's header asks for a decision rather than an
  // inheritance): the panel scrolls in its own `overflow-y: auto` container, which clips this element
  // before the implicit viewport root is ever consulted, so the 200px lead is inert and this fetches
  // at the edge of the scroller. Accepted for now — reaching the panel's scroll node means drilling a
  // ref down from the panel, which is a change to the panel's contract, not to this section.
  const { ref: sectionRef, visible } = useOnceVisible<HTMLDivElement>({ resetKey: entryId });

  // WP-7 — the selection is the PANEL's (shared with the parties field), and it is part of the
  // evidence query key: a filtered read is a different governed read, not a client-side view of the
  // same one, so the server decides which rows this caller sees under it.
  const party = useCrmPartyFilter();
  const evidence = useCrmRecordEvidence(listId, entryId, visible, party.selected);
  // ⛔ NO `limit` in the options: the hook's default IS `NOTES_PAGE`, and naming it here would be one
  // edit away from re-forking the query key this surface shares with `CrmNotesSection` (a fork = a
  // second audited `note_view` row). `enabled` is an option precisely because it is NOT in the key.
  const notes = useEntityNotes('list_entry', entryId, { enabled: visible });
  // A fixed three (hooks may not be called in a loop over changing data). Each is its own governed
  // read; one 403-ing does not touch the other two.
  const progress = useCrmFieldHistory(listId, entryId, UPDATE_LABELS[0].fieldKey, visible);
  const newInfo = useCrmFieldHistory(listId, entryId, UPDATE_LABELS[1].fieldKey, visible);
  const nextSteps = useCrmFieldHistory(listId, entryId, UPDATE_LABELS[2].fieldKey, visible);
  const updateQueries = [progress, newInfo, nextSteps];


  // F.5 — which evidence rows the operator has asked to read the body of, by item key. Transient
  // view state (no URL axis): a body read is an act, not a place, and it must not be re-issued by
  // someone opening a shared link. Collapsing removes the key, which unmounts the fetching child.
  //
  // ⛔ HOST-SCOPED, AND SCOPED DURING RENDER (review finding 5). The obvious fix — clearing this in
  // the `entryId` teardown effect below — is too late by one render: effects run AFTER the render for
  // the new host, by which time the fetching child has already mounted and issued its request. That is
  // a fetch-on-render path through the very affordance this item built to prevent one, and it was
  // measured: the effect version left the body hook called twice for one click. Carrying the host IN
  // the state and treating a mismatch as empty means the child cannot mount for the wrong record.
  const [openBodies, setOpenBodies] = useState<{ host: number; keys: string[] }>(
    { host: entryId, keys: [] },
  );
  // RESET, not just mask: masking alone kept the stale keys alive, so a ‹ › hop AWAY and BACK
  // re-mounted the fetching child with no click (measured — the test below hops both ways). This is
  // React's sanctioned "adjust state when a prop changes" — it re-renders before committing, so no
  // child ever sees the previous host's keys, and unlike an effect it costs no extra fetch.
  if (openBodies.host !== entryId) setOpenBodies({ host: entryId, keys: [] });
  const openKeys = openBodies.host === entryId ? openBodies.keys : [];
  const toggleBody = (key: string) =>
    setOpenBodies((s) => {
      const keys = s.host === entryId ? s.keys : [];
      return {
        host: entryId,
        keys: keys.includes(key) ? keys.filter((x) => x !== key) : [...keys, key],
      };
    });

  // ⛔ E1b-0 — the open refute dialog, held HERE rather than on the row: on success the row leaves
  // the evidence payload, so a dialog mounted inside it would take its own confirmation down with it.
  // The subject is COPIED into the state for the same reason — it must still be nameable after the
  // row it came from is gone.
  const [refuteFor, setRefuteFor] =
    useState<{ eventLogId: number; subject: string | null } | null>(null);

  // Kind filter (spec §8). Empty = All. Transient view state per the nav spec (a filter inside a
  // selection is not a drill-down view), and multi-select like the entity timeline's type chips.
  const [kinds, setKinds] = useState<TimelineKind[]>([]);
  const toggleKind = (k: TimelineKind) =>
    setKinds((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));

  // D3 — the date axis, ONE value, transient like the kind chips (a filter inside a selection is not
  // a drill-down view).
  //
  // A preset switch carries `from` / `to` forward untouched, deliberately: `splitByRange` reads them
  // only under `custom`, so flipping to 30 days and back returns the operator to what they typed
  // instead of to two empty boxes. A typed range costs far more to re-enter than a chip costs to
  // re-click, which is the whole reason this is not `setRange({ preset, from: '', to: '' })`.
  const [range, setRange] = useState<TimelineRange>(RANGE_DEFAULT);
  // ⭐ D2 AMENDED — and this flag is the whole reason the amendment is not a one-line default change.
  // The opening view (`false`) is a STATE OF ITS OWN, not a selection on the axis above: it reaches
  // back until it has 50 rows, so no chip on that bar describes it. `range` still holds
  // `RANGE_DEFAULT` underneath, so the `30 days` chip is where the operator lands if they *do* choose
  // the plain range — but until they choose something, nothing on the date axis is pressed except
  // `Recent`, and no date filters the stream.
  const [rangeChosen, setRangeChosen] = useState(false);
  /** Every date-axis control goes through this: choosing a range REPLACES the opening view. */
  const chooseRange = (next: (r: TimelineRange) => TimelineRange) => {
    setRangeChosen(true);
    setRange(next);
  };

  // D2 — the window. HOST-SCOPED AND RESET DURING RENDER, the `openBodies` pattern above and for the
  // same measured reason: the effect version runs after the render for the new record, so the previous
  // deal's "+150 shown" would paint once against the new deal's rows.
  //
  // `limit` is the operator's own floor (50, +50 a click). The OPENING view's floor is computed below
  // from the data and can be larger; `Math.max` of the two means the first click after a 30-day set of
  // 80 rows adds a full 50 rather than 20, and from then on `limit` alone rules.
  const [win, setWin] = useState<{ host: number; limit: number }>(
    { host: entryId, limit: WINDOW_STEP },
  );
  if (win.host !== entryId) setWin({ host: entryId, limit: WINDOW_STEP });
  const windowFloor = win.host === entryId ? win.limit : WINDOW_STEP;

  // WP-7 — "show emails" on a party chip sets the filter several inches above this section, so the
  // section brings itself into view. Keyed on a NONCE rather than on the selection: clicking the same
  // firm twice is two requests to look at it, and a selection-keyed effect would honour only the
  // first. Guarded on `> 0` so an ordinary mount never scrolls the panel.
  useEffect(() => {
    if (party.scrollNonce > 0) {
      // Optional-called: `scrollIntoView` is absent in jsdom and in some embedded webviews, and
      // scrolling is an affordance rather than a step in the filter — the selection is already
      // applied by the time this runs, so a missing method must not throw over a working filter.
      sectionRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [party.scrollNonce]);

  const { value: noteParam, open: openNote, close: closeNote } = useSelectionParam('note');

  // §7.6 teardown (review finding 6): drop the note param when the HOST RECORD changes — not on mount,
  // so a deep-linked note still opens. Without this the overlay survived a ‹ › hop and re-hosted onto
  // the next entry: the same leak class as the `?msg=` one the F.2–F.4 review caught. Mirrors
  // CrmNotesSection's prevKey guard.
  const prevHost = useRef(`list_entry:${entryId}`);
  useEffect(() => {
    const key = `list_entry:${entryId}`;
    if (prevHost.current !== key) {
      prevHost.current = key;
      if (noteParam) closeNote();
      // NB: expanded bodies are deliberately NOT torn down here — `openBodies` carries its own host
      // and is scoped during render instead, because this effect runs one render too late to stop a
      // fetch. See the state declaration above.
    }
  }, [entryId, noteParam, closeNote]);

  const evidenceRows = evidence.data?.evidence ?? [];
  const hiddenEvidence = evidence.data?.hidden_count ?? 0;
  // ⛔ The block's ABSENCE is the dark signal. `party_filter` rides only a flag-ON payload, so a
  // missing block means the feature does not exist here — not that the deal has no parties — and the
  // chip row, the kind lock and the empty-state wording all stay off together.
  const partyBlock = evidence.data?.party_filter;
  const partyChips = partyBlock?.parties ?? [];
  const partyOn = party.available && partyBlock != null;
  const partySelected = partyOn ? party.selected : [];
  const partyActive = partySelected.length > 0;
  const partyName = (id: string) => partyChips.find((c) => c.id === id)?.name ?? 'this party';
  // D-12: a party selection is a statement about MAIL. Notes and accepted updates carry no party, so
  // the lane set is pinned to Evidence for as long as one is chosen — and `kinds` is left untouched
  // underneath, so clearing the party returns the operator to the chips they had set.
  const kindsInEffect: TimelineKind[] = partyActive ? ['evidence'] : kinds;
  const updateStreams = UPDATE_LABELS.map(({ label }, i) => ({
    label, entries: updateQueries[i].data?.entries ?? [],
  }));
  const noteRows = notes.data?.items ?? [];
  const notesTruncated = notes.data?.has_more === true;

  // Merged on every render rather than memoized: three short lists, and a memo here would have to key
  // on five query payloads — an easy place to freeze a stale lane after a refetch.
  const updateItems = updatesLaneItems(updateStreams);
  const merged = mergeTimeline([
    evidenceLaneItems(evidenceRows),
    updateItems,
    // Twin suppression pairs against the updates lane — hence the argument rather than a global.
    notesLaneItems(noteRows, updateItems),
  ]);

  const showingKind = (k: TimelineKind) =>
    kindsInEffect.length === 0 || kindsInEffect.includes(k);

  // The kind filter runs first FOR COUNTING, which is not a departure from the spec's
  // "range -> kind -> window" but the thing that makes the range footers true. Both are filters, so
  // the rows on screen are identical either way (`inRange && inKind`, in merge order); what differs
  // is the four counts. Split over the whole stream, "+161 older than this range" under a Notes-only
  // filter would promise 161 rows that switching to All time never reveals — a count describing
  // something other than what the operator is looking at, which is the one thing D2 forbids a footer.
  const inKind = kindsInEffect.length === 0 ? merged
    : merged.filter((it) => kindsInEffect.includes(it.kind));
  // ⭐ THE OPENING VIEW FILTERS NO DATES. `RANGE_ALL` here is not "the operator picked All time" — it
  // is "no date bound has been chosen", which is what makes the amended ruling honest: nothing is
  // held back by a range nobody set, so `heldByRange` is 0, the escape footers stay silent because
  // they have nothing true to say, and the empty state is allowed to mean the deal is empty again.
  const split = splitByRange(inKind, rangeChosen ? range : RANGE_ALL);

  // ⭐ THE ITEM FLOOR, AND IT IS MEASURED AFTER THE KIND FILTER (amended D2, knock-on 3). Counting the
  // 30-day set over the whole stream would let a Notes-only view on a quiet deal fall under 50 rows
  // with the floor never firing — the floor would be describing rows the operator is not looking at,
  // which is the same defect the range footers were fixed for. Zero under a chosen range: a range the
  // operator typed may not be quietly widened to make 50 rows.
  const recentFloor = rangeChosen ? 0 : splitByRange(inKind, RANGE_DEFAULT).shown.length;
  const windowLimit = Math.max(windowFloor, recentFloor);
  const shown = split.shown.slice(0, windowLimit);
  // What the window is holding back — never what the RANGE is holding back (that is `heldByRange`).
  const beyondWindow = split.shown.length - shown.length;

  // How many rows THIS RANGE is holding back. Derived from `splitByRange`'s own four-way accounting
  // invariant rather than by re-testing "is a range in effect?" — a second copy of that predicate is
  // exactly the restated-gate class this codebase keeps paying for. It also gets the two "no range
  // after all" cases right for free: `all`, and `Custom…` with neither date typed, both of which
  // return every row in `shown` and zero everywhere else.
  const heldByRange = split.olderCount + split.newerCount + split.undated.length;

  const evidenceFailed = laneError(evidence.error);
  const updatesFailed = updateQueries.some((q) => updatesLaneError(q.error));
  const notesFailed = laneError(notes.error);
  const anyLoading = evidence.isLoading || notes.isLoading || updateQueries.some((q) => q.isLoading);

  return (
    // ⛔ THE REF GOES ON THE WRAPPER, and it must be attached in the FIRST render: `useOnceVisible`
    // builds its observer once per (key, not-yet-visible) with no re-arm on a late attach, so a
    // conditionally-rendered target would never arm at all. The header and the two filter bars give
    // this element height before any lane has answered, which is what makes it observable.
    <div ref={sectionRef} className={styles.wrap} data-testid="crm-record-timeline">
      {/* E1b-2 — the section's own header. `+ Note` is the ONLY note-creation entry point on a deal
          record now that the Notes sub-tab is gone (F.1 retires the notes DISPLAY block and keeps
          creation), and it reuses the overlay the note rows already open: the `note` selection param
          with the 'new' sentinel, so it is shareable, Back-closable and torn down with the host. */}
      <div className={styles.head}>
        <h3 className={styles.title}>Timeline</h3>
        <button type="button" className={styles.newNote} onClick={() => openNote('new')}>
          + Note
        </button>
      </div>

      {/* D-12 — with a party chosen the kind axis is LOCKED to Evidence: the chips stay on screen
          (a control that vanishes is a control the operator cannot find again) but are disabled and
          say why, and `Evidence` reads pressed because that is what is on screen. */}
      <div className={styles.filters} role="group" aria-label="Filter the timeline by kind">
        <button type="button" aria-pressed={!partyActive && kinds.length === 0} data-kind-filter="all"
          disabled={partyActive} title={partyActive ? KIND_LOCK_HINT : undefined}
          className={`${styles.chip} ${!partyActive && kinds.length === 0 ? styles.chipActive : ''}`}
          onClick={() => setKinds([])}>All</button>
        {KIND_CHIPS.map(({ kind, label }) => {
          const on = partyActive ? kind === 'evidence' : kinds.includes(kind);
          return (
            <button key={kind} type="button" aria-pressed={on} data-kind-filter={kind}
              disabled={partyActive} title={partyActive ? KIND_LOCK_HINT : undefined}
              className={`${styles.chip} ${on ? styles.chipActive : ''}`}
              onClick={() => toggleKind(kind)}>{label}</button>
          );
        })}
      </div>

      {/* WP-7 — the party axis. Only the parties with mail in this selection get a chip (a chip
          reading 0 is an affordance that leads nowhere), plus any party the operator has SELECTED,
          which must stay clickable to be unclickable. */}
      {partyOn && (partyChips.some((c) => c.count > 0) || partyActive) && (
        <div className={styles.filters} role="group" aria-label="Filter the timeline by party">
          <button type="button" aria-pressed={!partyActive} data-party-filter="all"
            className={`${styles.chip} ${!partyActive ? styles.chipActive : ''}`}
            onClick={() => party.clear()}>Anyone</button>
          {partyChips
            .filter((chip) => chip.count > 0 || partySelected.includes(chip.id))
            .map((chip) => {
              const on = partySelected.includes(chip.id);
              return (
                <button key={chip.id} type="button" aria-pressed={on} data-party-filter={chip.id}
                  className={`${styles.chip} ${on ? styles.chipActive : ''}`}
                  title={`${chip.name ?? 'This party'} — to, from or cc on ${chip.count} `
                    + `email${chip.count === 1 ? '' : 's'} you can see on this deal`}
                  onClick={() => party.toggle(chip.id)}>
                  {chip.name ?? '(unnamed)'}
                  <span className={styles.chipCount}>{chip.count}</span>
                </button>
              );
            })}
        </div>
      )}

      {/* D3 — the date axis. Same chip language as the kind bar above because it is the same kind of
          control; `Custom…` reveals the two native pickers, cross-constrained, mirroring the
          LLM-spend chart's window control so the firm's two date-window controls read as one idea. */}
      <div className={styles.filters} role="group" aria-label="Filter the timeline by date">
        {/* ⭐ THE OPENING VIEW'S OWN LABEL (amended D2). Deliberately NOT a `data-range-filter` and
            NOT a member of `TimelinePreset`: it is not a range, it is "the last 30 days, or more if
            30 days is thin", and putting it in the preset enum is exactly how a control starts
            claiming a range it is not honouring. It stays clickable so the operator can get BACK to
            it after choosing a range — a state you can only reach by reopening the record is a state
            the operator will read as a bug. The rule is on the chip itself, because a bare "Recent"
            does not tell anyone how far back they are looking. */}
        <button type="button" aria-pressed={!rangeChosen} data-range-mode="recent"
          className={`${styles.chip} ${!rangeChosen ? styles.chipActive : ''}`}
          title={`The last 30 days — or the ${WINDOW_STEP} most recent items, whichever reaches further back`}
          onClick={() => setRangeChosen(false)}>Recent</button>
        {RANGE_PRESETS.map(({ key, label }) => {
          const on = rangeChosen && range.preset === key;
          return (
            <button key={key} type="button" aria-pressed={on} data-range-filter={key}
              className={`${styles.chip} ${on ? styles.chipActive : ''}`}
              onClick={() => chooseRange((r) => ({ ...r, preset: key }))}>{label}</button>
          );
        })}
        {rangeChosen && range.preset === 'custom' && (
          <>
            <label className={styles.dateField}>
              From
              <input type="date" aria-label="Timeline range start date" data-range-picker="from"
                value={range.from} max={range.to}
                onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} />
            </label>
            <label className={styles.dateField}>
              To
              <input type="date" aria-label="Timeline range end date" data-range-picker="to"
                value={range.to} min={range.from}
                onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} />
            </label>
          </>
        )}
      </div>

      {/* ⛔ THE DEFERRED BRANCH RENDERS THE MARKER AND NOTHING ELSE - not the empty state, and
          not rows either. Two traps, and the structure closes both: with the lanes disabled
          `shown.length === 0 && !isLoading` is true before anything has been asked, so an ungated
          empty state prints "Nothing recorded on this deal yet" on a deal with years of history; and
          a disabled query still serves whatever is ALREADY IN THE CACHE (the notes key is
          byte-shared with `CrmNotesSection` on this same panel, and the other three carry a 60s
          `staleTime`), so "nothing is in the payload yet" was never a safe thing to lean on. Keeping
          the whole body inside this branch makes "nothing renders until someone looks" a property of
          the tree rather than a property of five payloads. */}
      {!visible ? (
        <div className={styles.empty} data-testid="crm-timeline-deferred">
          The timeline loads when you scroll to it — nothing has been read from this deal yet.
        </div>
      ) : (
        <>
        {/* Per-lane degradation: the failing lane says so where it would have rendered; the others are
            already merged above and stay on screen. */}
        {evidenceFailed && (
          <div className={styles.laneError} role="status" data-lane-error="evidence">
            Evidence couldn’t be loaded — the rest of the timeline is unaffected.
          </div>
        )}
        {updatesFailed && (
          <div className={styles.laneError} role="status" data-lane-error="updates">
            Accepted updates couldn’t be loaded — the rest of the timeline is unaffected.
          </div>
        )}
        {notesFailed && (
          <div className={styles.laneError} role="status" data-lane-error="notes">
            Notes couldn’t be loaded — the rest of the timeline is unaffected.
          </div>
        )}

        {/* D3 — an impossible range REFUSES, with its reason, rather than quietly showing everything or
            quietly showing nothing. One sentence for both causes (an unparseable value, `from` after
            `to`) on purpose: `splitByRange` owns that predicate and reports one verdict, and a component
            re-deriving WHICH cause fired would be a second copy of the rule it just called. */}
        {split.invalid ? (
          <div className={styles.rangeInvalid} role="status" data-testid="crm-timeline-range-invalid">
            That date range can’t be used — check that From and To are real dates, and that From is on or
            before To.
          </div>
        ) : (
          <>
            {anyLoading && shown.length === 0 && <div className={styles.empty}>Loading timeline…</div>}
            {/* A CHOSEN RANGE MAKES THIS SENTENCE LOAD-BEARING. "Nothing recorded on this deal yet."
                becomes a lie the moment a filter is what emptied the stream, and it is a lie told on
                the one surface whose entire job is history. `heldByRange` is the honest test for that:
                if the range is holding rows back, the range gets named, and the footers below carry
                the one-click way out.
                ⭐ AMENDED D2 — on the OPENING view `heldByRange` is 0 by construction (no date bound),
                so this falls through to the kind-only wording, or with no filter at all back to
                "Nothing recorded on this deal yet", which is TRUE again there. That is the ruling's
                third knock-on: G5c narrows to a CHOSEN range rather than being retired. */}
            {!anyLoading && shown.length === 0 && (
              <div className={styles.empty} data-testid="crm-timeline-empty">
                {/* ⛔ D-12's sentence, and it comes FIRST: with a party chosen the stream is the
                    evidence lane alone, so "nothing recorded on this deal yet" would be a lie told
                    on top of two lanes the operator can get back in one click. It names the party,
                    names the lane rule, and the `Anyone` chip above is the way out. */}
                {partyActive
                  ? `No emails with ${partySelected.map(partyName).join(' or ')} `
                    + `${heldByRange > 0 ? `in ${rangeName(range)}` : 'on this deal'}. `
                    + 'Notes and updates aren’t filtered by a party — choose Anyone to see them.'
                  : heldByRange > 0
                    ? `Nothing in ${rangeName(range)}${kinds.length > 0 ? ' matches that filter' : ''}.`
                    : kinds.length > 0
                      ? 'Nothing on this record matches that filter.'
                      : 'Nothing recorded on this deal yet.'}
              </div>
            )}
          </>
        )}

        {shown.length > 0 && (
          <ul className={styles.stream} data-testid="crm-timeline-stream">
            {shown.map((it) => (
              <li key={it.key} className={styles.row} data-lane={it.kind} data-item-key={it.key}
                data-sort-key={it.sortKey ?? ''}>
                <div className={styles.rowHead}>
                  <span className={styles.rowWhen}>{fmtWhen(it.sortKey)}</span>
                  <span className={`${styles.kind} ${styles[`kind_${it.kind}`]}`}>{it.kind}</span>
                  {it.noteId != null ? (
                    <button type="button" className={styles.rowTitleBtn}
                      onClick={() => openNote(String(it.noteId))}>{it.title}</button>
                  ) : (
                    <span className={styles.rowTitle} title={it.title}>{it.title}</span>
                  )}
                  {/* Parity with the Evidence sub-tab: one email seen by N colleagues is ONE row, and
                      without this badge it read as an ordinary single email. */}
                  {(it.inboxCount ?? 1) > 1 && (
                    <span className={styles.inboxBadge}
                      title={`Same email scanned from ${it.inboxCount} Ember mailboxes`}>
                      +{it.inboxCount} inboxes
                    </span>
                  )}
                  {/* E1b-0 — rendered from the SERVER's `can_refute` for this row and this caller. */}
                  {it.kind === 'evidence' && it.canRefute && it.evidenceEventId != null && (
                    <button type="button" className={styles.refute} title="Refute evidence"
                      aria-label="Refute evidence"
                      data-refute-event={it.evidenceEventId}
                      onClick={() => setRefuteFor({
                        eventLogId: it.evidenceEventId as number, subject: it.title,
                      })}>⚑</button>
                  )}
                </div>
                {it.meta && <div className={styles.rowMeta}>{it.meta}</div>}
                {/* E1b-3 D4 — the clamp wraps THE ROW'S NARRATIVE AND NOTHING ELSE. ⛔ Deliberately
                    outside it, above and below: the whole `rowHead` (when / kind / the note-opening
                    title button / the "+N inboxes" badge / the ⚑ refute), `rowMeta`, the "Show message"
                    toggle, and the mounted body. A clamp that can hide a governed affordance is a
                    governance bug wearing CSS, so the structure — not a style — is what keeps them out,
                    and gate G8 asserts they are not descendants of the clamped element.

                    ⛔ THE `key` IS REQUIRED, and the `li`'s own key does NOT cover it. `expanded` is
                    instance state (ClampedBlock's header), and the li is only remounted when its key
                    changes — but an updates row's key is `updates:<label>:<version>`, which is scoped to
                    the ENTRY, not global. Two deals whose Deal Progress both sit at version 4 produce
                    the SAME li key, so a ‹ › hop between them reuses the li and would carry one
                    record's "I have read this" onto another record's bullets. Keying by entryId as well
                    makes that impossible; the hop is pinned by test. */}
                <ClampedBlock key={`clamp:${entryId}:${it.key}`} maxHeight={ROW_CLAMP} label={it.title}>
                  <CrmBlockRuns groups={it.groups} tone="plain" listClassName={styles.bullets} />
                  {it.body && <div className={styles.rowBody}>{it.body}</div>}
                </ClampedBlock>
                {/* F.5 — the evidence lane's body is an EXPANDER, never inline text. Offered only where
                    the server said the body is readable (`bodyAvailable` — the same gate that used to
                    populate the removed inline preview), and the read happens when the child mounts on
                    the click. */}
                {it.kind === 'evidence' && it.bodyAvailable && it.evidenceEventId != null && (
                  <>
                    <button type="button" className={styles.bodyToggle}
                      data-evidence-toggle={it.evidenceEventId}
                      aria-expanded={openKeys.includes(it.key)}
                      onClick={() => toggleBody(it.key)}>
                      {openKeys.includes(it.key) ? 'Hide message' : 'Show message'}
                    </button>
                    {openKeys.includes(it.key) && (
                      <TimelineEvidenceBody listId={listId} entryId={entryId}
                        eventLogId={it.evidenceEventId} />
                    )}
                  </>
                )}
              </li>
            ))}
          </ul>
        )}

        {/* ⭐ D2 — THE WINDOW'S FOOTER, AND IT NAMES THE SET IT IS COUNTING (amended ruling, knock-on
            1: `M` must never be a 30-day window the opening view has already reached past). The scope
            word is derived from `heldByRange` rather than from "is a range chosen?", which makes the
            sentence true in every state for ONE reason instead of four: `heldByRange > 0` is exactly
            "the range is excluding rows", so `M` is the in-range total and the sentence says so; at
            zero `M` IS the post-kind-filter total for the deal — on the opening view, which excludes
            nothing by date, and equally under a chosen range that happens to hold nothing back.
            "the N most recent" is safe throughout: the merge is newest-first and undated rows sort
            last, so an undated row can only be inside the window once every dated row is — at which
            point nothing is held back and this footer does not render at all.
            Rendered only when the window is actually holding rows back (§4.4: a footer speaks when it
            has something true to say); the `+ Show N more` beside it is the escape, so this count is
            never a dead end either — the same rule the range footers ship under. */}
        {beyondWindow > 0 && (
          <div className={styles.windowFoot} data-testid="crm-timeline-window">
            <span className={styles.hidden}>
              Showing the {shown.length} most recent of {split.shown.length}
              {heldByRange > 0 ? ' in this date range' : ' on this deal'}
            </span>
            {/* `Math.min` rather than a flat "+ Show 50 more": a control offering 50 when 12 remain is
                the same class of small lie as a count that describes the wrong set. */}
            <button type="button" className={styles.rangeEscape} data-testid="crm-timeline-show-more"
              onClick={() => setWin({ host: entryId, limit: windowLimit + WINDOW_STEP })}>
              + Show {Math.min(WINDOW_STEP, beyondWindow)} more
            </button>
          </div>
        )}

        {/* THE RANGE COUNTS ARE ACTIONS, NOT NOTES — the condition a CHOSEN range ships under. A count
            the operator cannot act on is a dead end at exactly the moment they need the rows it is
            counting. Both switch the axis to All time and leave the kind filter alone: widening time is
            what the count promises, so widening time is all it may do. Neither needs a `showingKind`
            guard — they are already counted over the kind-filtered stream. Neither can fire on the
            opening view, which holds nothing back by date; the window's footer above is that view's
            escape, and it is a button for the same reason. */}
        {split.olderCount > 0 && (
          <button type="button" className={styles.rangeEscape} data-testid="crm-timeline-range-older"
            onClick={() => setRange(RANGE_ALL)}>
            +{split.olderCount} older than this range — show all time
          </button>
        )}
        {split.newerCount > 0 && (
          <button type="button" className={styles.rangeEscape} data-testid="crm-timeline-range-newer"
            onClick={() => setRange(RANGE_ALL)}>
            +{split.newerCount} newer than this range — show all time
          </button>
        )}
        {/* A plain note, unlike the two above: "undated" is not a range the operator can widen TO, so a
            button here would promise a filter that does not exist. It is still said out loud, because a
            row dropped for having no timestamp is history vanishing with no trace. */}
        {split.undated.length > 0 && (
          <div className={styles.hidden} data-testid="crm-timeline-range-undated">
            +{split.undated.length} undated — no timestamp to place{' '}
            {split.undated.length === 1 ? 'it' : 'them'} in a range
          </div>
        )}

        {/* Both footers are LANE-SCOPED (review finding 10): a hidden-evidence count under a notes-only
            stream reads as "notes are being withheld", which is not what either number means. Both are
            also silenced by a REFUSED range: they describe the PAYLOAD rather than the window, so
            leaving "+3 more emails not scanned from your mailbox" under "that date range can't be used"
            offers a second, unrelated reason for an emptiness the refusal has already explained. */}
        {notesTruncated && showingKind('notes') && !split.invalid && (
          <div className={styles.hidden} data-testid="crm-timeline-notes-truncated">
            Showing the {NOTES_PAGE} most recent notes — older ones are not in this list.
          </div>
        )}
        {hiddenEvidence > 0 && showingKind('evidence') && !split.invalid && (
          <div className={styles.hidden} data-testid="crm-timeline-evidence-hidden">
            +{hiddenEvidence} more email{hiddenEvidence === 1 ? '' : 's'} not scanned from your mailbox
          </div>
        )}

        </>
      )}

      {/* ⛔ AT THE ROOT, and it must stay here. See the state declaration. */}
      {refuteFor && (
        <CrmRefuteEvidenceModal listId={listId} entryId={entryId} eventLogId={refuteFor.eventLogId}
          subject={refuteFor.subject} onClose={() => setRefuteFor(null)} />
      )}

      {/* The Notes sub-view owns this overlay on its own tab; on THIS tab nothing else does, so a note
          row would open to nothing without it. Same host + param contract as CrmNotesSection. */}
      {noteParam && (
        <CrmNoteOverlay noteParam={noteParam} hostKind="list_entry" hostId={entryId}
          hostLabel={hostLabel} mentionContext={mentionContext}
          onClose={closeNote} onCreated={(nid) => openNote(String(nid), { replace: true })} />
      )}
    </div>
  );
}

export default CrmRecordTimeline;
